/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState, useCallback } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { Dropdown } from 'antd'

import { Flex } from '../../Layout'
import OptionButtonIcon from './Icon'

export const MoreOptionListItem = ({
  onClick,
  textColor,
  iconName,
  title,
  iconColor,
  faType,
}) => {
  return (
    <Flex
      className='more-option-list-item'
      alignItems='center'
      onClick={onClick}
      css={css`
        background-color: ${colors.HEADER};
        padding: 12px 20px;

        cursor: pointer;

        p,
        i {
          transition: color 0.15s ease-in-out;
          color: ${textColor};
        }

        transition: background-color 0.15s ease-in-out;

        &:hover {
          background-color: ${colors.SOFT_STEEL};

          p,
          i {
            color: white;
          }
        }
      `}
    >
      <OptionButtonIcon
        name={iconName}
        title={title}
        faType={faType}
        color={iconColor}
        css={css`
          margin-right: 8px;
        `}
      />
      <p
        css={css`
          text-transform: capitalize;
        `}
      >
        {title}
      </p>
    </Flex>
  )
}

const dropdownMenuStyle = css`
  width: max-content;
  border: 1px solid ${colors.SOFT_STEEL};
  box-shadow: -4px 4px 4px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  border-radius: 4px;
  background-color: ${colors.HEADER};

  .more-option-list-item {
    &:not(:last-of-type) {
      border-bottom: 1px solid ${colors.SOFT_STEEL};
    }
  }
`

const dropdownOverlayStyle = { zIndex: 9999 }

const MoreOptions = ({ options: _options = [] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const options = _options.filter((opt) => {
    // Option can resolve to undefined (e.g. did not pass permission check)
    // so filter those out here
    if (typeof opt !== 'object') {
      return false
    }
    return Object.values(opt)?.length > 0
  })

  const closeDropdown = useCallback(() => setIsOpen(false), [])

  const renderDropdownContent = useCallback(
    () => (
      <div css={dropdownMenuStyle}>
        {options.map((option) => {
          const {
            key,
            title,
            onClick,
            iconName,
            iconColor = 'white',
            textColor = 'white',
            Component,
            componentProps,
          } = option

          if (Component) {
            return (
              <Component
                key={key}
                option={option}
                onToggle={closeDropdown}
                {...componentProps}
              />
            )
          }

          return (
            <MoreOptionListItem
              key={key}
              title={title}
              onClick={() => {
                closeDropdown()
                onClick()
              }}
              iconName={iconName}
              iconColor={iconColor}
              textColor={textColor}
            />
          )
        })}
      </div>
    ),
    [options, closeDropdown]
  )

  if (options.length === 0) {
    return false
  }

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={setIsOpen}
      placement='bottomRight'
      trigger={['click']}
      dropdownRender={renderDropdownContent}
      overlayStyle={dropdownOverlayStyle}
    >
      <button
        type='button'
        className='option-button-delete-btn'
        css={css`
          width: 20px;

          ${isOpen &&
          `
            i {
              color: white;
            }
          `}

          &:hover {
            i {
              color: white;
            }
          }
        `}
      >
        <OptionButtonIcon name='ellipsis-v' title='More options' />
      </button>
    </Dropdown>
  )
}

export default MoreOptions
